import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { io, Socket } from "socket.io-client";
import { toast } from "sonner";
import { MessageSquare, Video, Phone } from "lucide-react";

import { useGracePeriodAlert } from "../../hooks/connectivityHooks/useGracePeriodAlert";

// 🟢 Redux hooks
import { useDispatch, useSelector } from "react-redux";
import { setActiveSession } from "../../store/slices/activeSessionSlice";

const SOCKET_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:3000";

interface ExpertSocketContextType {
  socket: Socket | null;
}

interface IncomingRequestData {
  sessionId: string;
  sessionType?: string;
  rtcToken?: string;
  channelName?: string;
}

const ExpertSocketContext =
  createContext<ExpertSocketContextType>({
    socket: null,
  });

export const useExpertSocket = () =>
  useContext(ExpertSocketContext);

export const ExpertSocketProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [socket, setSocket] =
    useState<Socket | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  const dispatch = useDispatch();

  // Pull the active session from Redux
  const activeSession = useSelector(
    (state: any) => state.activeSession?.session,
  );

  // Track active session inside Socket listener closures
  const activeSessionRef =
    useRef(activeSession);

  useEffect(() => {
    activeSessionRef.current =
      activeSession;
  }, [activeSession]);

  const expertCode =
    localStorage.getItem("expertCode") ||
    localStorage.getItem("expert_code");

  const {
    isGracePeriodActive,
    disconnectedRole,
    disconnectedName,
    formattedRemainingTime,
  } = useGracePeriodAlert({
    socket,
    sessionId:
      activeSession?.sessionId || "",
    senderCode: expertCode || "",
    senderType: "expert",
  });

  /*
   * Grace-period UI starts in the center.
   * After five seconds it moves to the footer.
   */
  const [
    isGracePeriodFooter,
    setIsGracePeriodFooter,
  ] = useState(false);

  useEffect(() => {
    if (!isGracePeriodActive) {
      setIsGracePeriodFooter(false);
      return;
    }

    const timer = window.setTimeout(() => {
      setIsGracePeriodFooter(true);
    }, 5000);

    return () => {
      window.clearTimeout(timer);
    };
  }, [isGracePeriodActive]);

  const locationRef =
    useRef(location);

  useEffect(() => {
    locationRef.current =
      location;
  }, [location]);

  const audioRef =
    useRef<HTMLAudioElement | null>(
      null,
    );

  const activeToastSessionIds =
    useRef<Set<string>>(
      new Set(),
    );

  const incomingRequestMap =
    useRef(
      new Map<
        string,
        {
          toastId: string;
          ringAudio: HTMLAudioElement;
        }
      >(),
    );

  const acceptIncomingRequest = (
    data: IncomingRequestData,
    incomingType: string,
    expertCode: string,
    ringAudio: HTMLAudioElement,
    toastId: string,
  ) => {
    activeToastSessionIds.current.delete(
      data.sessionId,
    );

    incomingRequestMap.current.delete(
      data.sessionId,
    );

    ringAudio.pause();

    toast.dismiss(toastId);

    const sessionData = {
      sessionId: data.sessionId,
      rtcToken: data.rtcToken,
      channelName: data.channelName,
      type: incomingType,
      expertCode: expertCode || "",
    };

    dispatch(
      setActiveSession(sessionData),
    );

    navigate(
      `/${incomingType}/${expertCode}`,
      {
        state: {
          ...sessionData,
          isInitiator: false,
        },
      },
    );
  };

  const rejectIncomingRequest = (
    data: IncomingRequestData,
    ringAudio: HTMLAudioElement,
    toastId: string,
    socket: Socket,
    expertCode: string,
  ) => {
    activeToastSessionIds.current.delete(
      data.sessionId,
    );

    incomingRequestMap.current.delete(
      data.sessionId,
    );

    ringAudio.pause();

    toast.dismiss(toastId);

    socket.emit(
      "reject_session",
      {
        sessionId: data.sessionId,
        expertCode: expertCode,
      },
    );
  };

  useEffect(() => {
    const expertCode =
      localStorage.getItem(
        "expertCode",
      ) ||
      localStorage.getItem(
        "expert_code",
      );

    if (!expertCode) return;

    const newSocket = io(
      SOCKET_URL,
      {
        path: "/socket.io/",
        transports: ["websocket"],
        upgrade: false,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 10,
        query: {
          senderCode: expertCode,
          senderType: "expert",
        },
      },
    );

    setSocket(newSocket);

    newSocket.on(
      "connect",
      () => {},
    );

    newSocket.on(
      "reconnect",
      () => {},
    );

    // SINGLE-DEVICE LOGIN
    // Backend sends this when the Expert account is taken over
    // from another device/browser.
    const handleForceLogout = (
      data?: {
        reason?: string;
      },
    ) => {
      console.log(
        "🔐 Expert account logged out because it was taken over.",
        data?.reason,
      );

      // Stop any active incoming-call ringtone/toast.
      incomingRequestMap.current.forEach(
        (request) => {
          request.ringAudio.pause();
          toast.dismiss(
            request.toastId,
          );
        },
      );

      incomingRequestMap.current.clear();
      activeToastSessionIds.current.clear();

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      // Clear Expert authentication state.
      localStorage.removeItem(
        "expertCode",
      );

      localStorage.removeItem(
        "expert_code",
      );

      localStorage.removeItem(
        "expertName",
      );

      // Close this Expert's socket connection.
      newSocket.disconnect();

      toast.error(
        "Account Logged Out",
        {
          description:
            "Your Expert account was logged in on another device.",
        },
      );

      // Send the old device back to Expert login.
      navigate(
        "/expert-login",
        {
          replace: true,
        },
      );
    };

    newSocket.on(
      "expert_force_logout",
      handleForceLogout,
    );

    newSocket.on(
      "incoming_request",
      (
        data: {
          sessionId: string;
          sessionType?: string;
          rtcToken?: string;
          channelName?: string;
        },
      ) => {
        if (
          activeSessionRef.current
            ?.sessionId ===
          data.sessionId
        ) {
          return;
        }

        if (
          activeToastSessionIds.current.has(
            data.sessionId,
          )
        ) {
          return;
        }

        activeToastSessionIds.current.add(
          data.sessionId,
        );

        const incomingType =
          data.sessionType || "chat";

        const currentLoc =
          locationRef.current;

        const isInChat =
          currentLoc.pathname.includes(
            "/chat/",
          );

        const isInCall =
          currentLoc.pathname.includes(
            "/call/",
          );

        const isInVideo =
          currentLoc.pathname.includes(
            "/video/",
          );

        if (
          isInChat ||
          isInCall ||
          isInVideo
        ) {
          if (
            currentLoc.state
              ?.sessionId ===
            data.sessionId
          ) {
            activeToastSessionIds.current.delete(
              data.sessionId,
            );

            return;
          }
        }

        if (audioRef.current) {
          audioRef.current.pause();
        }

        const ringAudio =
          new Audio(
            "/trueAdvisoRining.mp3",
          );

        ringAudio.loop = true;

        audioRef.current =
          ringAudio;

        ringAudio
          .play()
          .catch(() => {
            console.warn(
              "🔇 Browser blocked autoplay.",
            );
          });

        const uniqueToastId =
          `ring-${data.sessionId}-${Date.now()}`;

        incomingRequestMap.current.set(
          data.sessionId,
          {
            toastId:
              uniqueToastId,
            ringAudio,
          },
        );

        const isVideo =
          incomingType === "video";

        const isCall =
          incomingType === "call";

        let RingIcon =
          MessageSquare;

        if (isVideo) {
          RingIcon = Video;
        }

        if (isCall) {
          RingIcon = Phone;
        }

        toast.custom(
          () => (
            <div className="bg-white p-4 rounded-xl shadow-2xl border-2 border-emerald-500 flex flex-col gap-3 w-80">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-full animate-bounce">
                  <RingIcon className="text-emerald-600" />
                </div>

                <div>
                  <h3 className="font-bold capitalize">
                    Incoming{" "}
                    {incomingType}!
                  </h3>

                  <p className="text-xs text-gray-500">
                    A user is waiting.
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() =>
                    acceptIncomingRequest(
                      data,
                      incomingType,
                      expertCode ||
                        "",
                      ringAudio,
                      uniqueToastId,
                    )
                  }
                  className="flex-1 bg-emerald-500 text-white font-bold py-2 rounded-lg hover:bg-emerald-600 transition-colors"
                >
                  Accept
                </button>

                <button
                  onClick={() =>
                    rejectIncomingRequest(
                      data,
                      ringAudio,
                      uniqueToastId,
                      newSocket,
                      expertCode ||
                        "",
                    )
                  }
                  className="flex-1 bg-gray-100 text-gray-600 font-bold py-2 rounded-lg hover:bg-red-100 hover:text-red-600 transition-colors"
                >
                  Reject
                </button>
              </div>
            </div>
          ),
          {
            id: uniqueToastId,
            duration: 30000,
            onDismiss: () => {
              activeToastSessionIds.current.delete(
                data.sessionId,
              );

              incomingRequestMap.current.delete(
                data.sessionId,
              );

              ringAudio.pause();
            },
          },
        );
      },
    );

    // Stop ringtone & remove toast when caller cancels
    newSocket.on(
      "session_cancelled",
      ({
        sessionId,
      }: {
        sessionId: string;
      }) => {
        const request =
          incomingRequestMap.current.get(
            sessionId,
          );

        if (!request) return;

        request.ringAudio.pause();

        toast.dismiss(
          request.toastId,
        );

        incomingRequestMap.current.delete(
          sessionId,
        );

        activeToastSessionIds.current.delete(
          sessionId,
        );
      },
    );

    return () => {
      newSocket.off(
        "expert_force_logout",
        handleForceLogout,
      );

      newSocket.off(
        "session_cancelled",
      );

      newSocket.disconnect();

      if (audioRef.current) {
        audioRef.current.pause();
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ExpertSocketContext.Provider
      value={{ socket }}
    >
      {children}

      {isGracePeriodActive && (
        <div
          className={`fixed inset-0 z-[9999] pointer-events-none flex px-4 ${
            isGracePeriodFooter
              ? "items-end justify-center pb-5"
              : "items-center justify-center"
          }`}
        >
          <div
            className={`pointer-events-auto bg-white shadow-2xl border border-gray-200 ${
              isGracePeriodFooter
                ? "w-auto max-w-md rounded-xl px-4 py-2"
                : "w-full max-w-md rounded-2xl p-6"
            }`}
          >
            {!isGracePeriodFooter ? (
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-100">
                  <span className="text-2xl">
                    ⚠️
                  </span>
                </div>

                <div className="flex-1">
                  <h2 className="text-xl font-bold text-gray-900">
                    Attention
                  </h2>

                  <p className="mt-2 text-gray-600">
                    {disconnectedName ||
                      "The other participant"}{" "}
                    has left the
                    consultation.
                  </p>

                  <p className="mt-3 text-sm text-gray-500">
                    {disconnectedRole ===
                    "user"
                      ? "The user has temporarily disconnected."
                      : "The expert has temporarily disconnected."}
                  </p>

                  <div className="mt-5 rounded-xl bg-gray-100 px-4 py-3 text-center">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      Time left before automatic
                      disconnection
                    </p>

                    <p className="mt-1 font-mono text-3xl font-bold text-red-600">
                      {
                        formattedRemainingTime
                      }
                    </p>
                  </div>

                  <p className="mt-4 text-center text-xs text-gray-400">
                    The consultation will
                    continue automatically
                    if the participant
                    reconnects.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <span className="text-lg">
                  ⚠️
                </span>

                <div>
                  <p className="text-xs font-semibold text-gray-900">
                    {disconnectedName ||
                      "Participant"}{" "}
                    disconnected
                  </p>

                  <p className="text-[11px] text-gray-500">
                    Waiting for
                    reconnection
                  </p>
                </div>

                <div className="ml-2 rounded-lg bg-gray-100 px-3 py-1 text-center">
                  <p className="text-[9px] font-medium uppercase tracking-wide text-gray-500">
                    Time left
                  </p>

                  <p className="font-mono text-lg font-bold leading-5 text-red-600">
                    {
                      formattedRemainingTime
                    }
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </ExpertSocketContext.Provider>
  );
};